import React, { useEffect, useState } from 'react';
import PageHeader from '../../Common/PageHeader';
import Icons from '../../../../components/Common/Icons/Icons';
import ContentCard from '../../Common/Card';
import MenuIcon from '../../Layout/Menu/MenuIcon';
import Footer from '../../Layout/Footer';
import { accountData, IAccount, IAccountData } from '../../Layout/Account/Data';
import AccountItem from './Account';
import { Button } from '../../../../components/Common/UI/Button';
import FakePagination from '../../../../components/Common/UI/FakePagination';
import InfoHint from './Hint';

const viewerUpdatesRaw = localStorage.getItem('viewerUpdates');

const ALLOWED_ACCOUNTS_FOR_USER = viewerUpdatesRaw
  ? (JSON.parse(viewerUpdatesRaw).find((u: IAccountData) => u.id === '1')?.selectedAccounts ?? [])
  : ['****4560', '****4564'];

function getAccountsForCurrentUser(): IAccount[] {
  const tempViewers = JSON.parse(localStorage.getItem('tempViewers') || '[]');
  const isAuthorized = tempViewers.length > 0;

  let filteredGroups = isAuthorized
    ? accountData.filter(el => el.type === 'checking' || el.type === 'loan')
    : accountData;

  if (isAuthorized) {
    filteredGroups = filteredGroups
      .map(accountType => ({
        ...accountType,
        accounts: accountType.accounts.filter(acc => ALLOWED_ACCOUNTS_FOR_USER.includes(acc.number)),
      }))
      .filter(accountType => accountType.accounts.length > 0);
  }

  return filteredGroups.flatMap(el => el.accounts).map((acc, index) => ({ ...acc, id: index.toString() }));
}

const accounts = accountData
  .map(el => el.accounts)
  .flatMap(subArray => subArray)
  .map((el2, index) => ({ ...el2, id: index.toString() }));

function Profile() {
  const [state, setState] = useState<IAccount[]>([]);
  const [initial, setInitial] = useState<IAccount[]>([]);
  const [editAccountIds, setEditAccountIds] = useState<string[]>([]);
  const [editAll, setEditAll] = useState<boolean>(false);
  const [hint, setHint] = useState<boolean>();

  useEffect(() => {
    const data = getAccountsForCurrentUser();

    setState(data);
    setInitial(data);
  }, []);

  const handleHint = (payload?: boolean) => {
    setHint(payload);
  };

  const handleChange = (payload: IAccount) => {
    const newData = state.map(el => (el.id === payload.id ? payload : el));

    setState(newData);
  };

  const handleSave = (payload: IAccount) => {
    const newData = state.map(el => (el.id === payload.id ? payload : el));

    setState(newData);
  };

  const handleEditMode = (ids: string[]) => {
    const idsToRemove = new Set(editAccountIds.filter(id => ids.includes(id)));
    const newIds = ids.filter(id => !idsToRemove.has(id));
    const updatedIds = [...editAccountIds.filter(id => !ids.includes(id)), ...newIds];

    setEditAccountIds(updatedIds);
  };

  const handleEditAll = () => {
    setEditAccountIds(state.map(el => el.id));
    setEditAll(true);
  };

  const handleSaveAll = () => {
    setHint(true);
    setInitial(state);
    setEditAccountIds([]);
    setEditAll(false);
  };

  const handleCancelAll = () => {
    setHint(false);
    setState(initial);
    setEditAccountIds([]);
    setEditAll(false);
  };

  return (
    <div className="grid grid-rows-[auto,1fr]">
      <PageHeader icon={<Icons.UserCheck />} title="Profile" />
      <div className="px-[20px] grid gap-[20px] grid-rows-[1fr,auto]">
        <ContentCard
          title="Account Preferences"
          icon={
            <MenuIcon icon={<Icons.UserCheck />} className="bg-primary border-primary" variant="white" size="large" />
          }
          actions={
            <div>
              {editAll ? (
                <div className="flex gap-[5px] justify-end items-center">
                  <Button
                    onClick={handleCancelAll}
                    className="p-[8px_10px] h-auto bg-addition10 hover:bg-addition10-80 min-w-[100px]"
                  >
                    <Icons.PlusFilled className="rotate-45" />
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveAll}
                    className="p-[8px_10px] h-auto bg-primary hover:bg-primary-80 min-w-[100px]"
                  >
                    <Icons.Check />
                    Save
                  </Button>
                </div>
              ) : (
                <Button
                  className="p-[8px_10px] h-auto flex items-center bg-primary hover:bg-primary-80"
                  onClick={handleEditAll}
                >
                  <Icons.EditPen className="h-[18px] w-[18px]" />
                  Edit all
                </Button>
              )}
            </div>
          }
        >
          <div
            className="grid xl:grid-cols-[repeat(3,minmax(auto,370px))] gap-x-[20px] xl:gap-y-[30px]
          items-start relative lg:grid-cols-2 gap-y-[20px]"
          >
            <InfoHint visible={hint} full handleHint={handleHint} />
            {state.map(el => (
              <AccountItem
                key={el.id}
                account={el}
                editAll={editAll}
                handleChange={handleChange}
                handleSave={handleSave}
                handleEditMode={handleEditMode}
                isEdit={editAccountIds.includes(el.id)}
              />
            ))}
          </div>
          <div className="mt-[30px] flex justify-end">
            <FakePagination value="1-5 of 5" />
          </div>
        </ContentCard>
        <Footer className="self-end" />
      </div>
    </div>
  );
}

export default Profile;
